import { useCallback, useEffect } from 'react';

import { Box, IconButton, Skeleton, useMediaQuery } from '@chakra-ui/react';

import { urlMap } from '@nocowanie/core';
import { IconsLinear } from '@nocowanie/icons';

import { SearchEngineContainerProps } from './search-engine-container.props';
import { useSearchEngineActions } from './use-search-engine-actions.hook';
import { useSearchEngineData } from './use-search-engine-data.hook';

import { CommonModalsEnum } from '../../../enums';
import { useFragmentActions, useHasMounted, useModalSync } from '../../../hooks';
import { defaultSearchEngineTranslation } from '../../../models';
import { AutocompleteBaseItemModel } from '../../molecules/autocomplete/autocomplete-base-item.model';
import { SearchEngineDesktop } from '../search-engine-desktop';
import { SearchEngineDesktopV7 } from '../search-engine-desktop-v7';
import { SearchEngineMobile } from '../search-engine-mobile';
import { SearchEngineMobileDrawer } from '../search-engine-mobile-drawer';

export const searchEngineIsMobileSsrBreakpoint = '1250px';
const legacyBreakpoint = '1500px';

export const SearchEngineContainer = <T extends AutocompleteBaseItemModel>({
    autocompleteData,
    isWebComponentVersion = false,
    initialFormData = {},
    onSearchEngineDataChanged,
    onSearchEngineSubmit,
    onDrawerOpen,
    onFormReady,
    mobileDrawerDisclosure,
    isV7SearchEngineVersion,
    isMobileCompactVersion = true,
    isMobileBrowser = false,
    baseUrl = urlMap.search,
    defaultBaseUrl = urlMap.search,
    isMapSearch = false,
    showSkeleton = false,
    ...rest
}: SearchEngineContainerProps<T>) => {
    const {
        seDesktopProps,
        seMobileProps,
        watch,
        getValues,
        handleSubmit,
        register,
        reset,
        isSubmitted,
        seAutocompleteData,
        setValue,
    } = useSearchEngineData({
        isWebComponentVersion,
        initialFormData,
        autocompleteData,
        onSearchEngineDataChanged,
        isMapSearch,
    });

    const { addFragment, removeFragment } = useFragmentActions(CommonModalsEnum.SearchEngine);

    const { onSubmit, open, onClose, onOpen, isMobileDrawerPending, handleDrawerOpen } =
        useSearchEngineActions({
            getValues,
            reset,
            baseUrl,
            defaultBaseUrl,
            mobileDrawerDisclosure,
            onSearchEngineSubmit,
            addFragment,
            autocompleteData,
            isMapSearch,
        });

    const [isMobile] = useMediaQuery(
        [
            `(max-width: ${
                isWebComponentVersion ? legacyBreakpoint : searchEngineIsMobileSsrBreakpoint
            })`,
        ],
        {
            fallback: [isMobileBrowser],
        },
    );

    useModalSync({
        modalName: CommonModalsEnum.SearchEngine,
        onOpen,
        onClose,
        isOpen: open,
    });

    const closeDrawer = useCallback(() => {
        onClose();
        if (isMapSearch) {
            window.location.hash = '';
        } else {
            removeFragment();
        }
    }, [isMapSearch, onClose, removeFragment]);

    const openDrawer = useCallback(() => {
        handleDrawerOpen();
        if (onDrawerOpen) {
            onDrawerOpen();
        }
    }, [handleDrawerOpen, onDrawerOpen]);

    useEffect(() => {
        if (isMapSearch && onFormReady) {
            onFormReady({
                setValue,
                onInputValueUpdated: () => {
                    setTimeout(() => {
                        const values = getValues();
                        onSearchEngineDataChanged?.(values);
                    }, 10);
                },
            });
        }
    }, [isMapSearch, onFormReady, setValue, getValues, onSearchEngineDataChanged]);

    const hasMounted = useHasMounted();

    // condition required only for webcomponent version - where desktop variant is initially rendered, which causes layout issues
    if (!hasMounted && isWebComponentVersion) {
        return null;
    }

    const searchEngineDesktopContent = isV7SearchEngineVersion ? (
        <SearchEngineDesktopV7 {...seDesktopProps} showSkeleton={showSkeleton} watch={watch} />
    ) : (
        <SearchEngineDesktop {...seDesktopProps} watch={watch} />
    );

    return (
        <Box {...rest}>
            <form onSubmit={handleSubmit(onSubmit)}>
                {isMobile ? (
                    <>
                        {isMobileCompactVersion ? (
                            <IconButton
                                onClick={openDrawer}
                                aria-label={defaultSearchEngineTranslation.buttonSubmitAriaLabel}
                                border={'none'}
                                loading={isMobileDrawerPending}
                                data-testid="search-engine-open-button"
                            >
                                <IconsLinear.Search size="md" />
                            </IconButton>
                        ) : (
                            <Skeleton loading={showSkeleton} asChild>
                                <SearchEngineMobile
                                    handleDrawerOpen={openDrawer}
                                    isDrawerPending={isMobileDrawerPending}
                                    {...seMobileProps}
                                />
                            </Skeleton>
                        )}
                        <SearchEngineMobileDrawer
                            onSubmit={handleSubmit(onSubmit)}
                            register={register}
                            isSubmitted={isSubmitted}
                            isOpen={open}
                            onClose={closeDrawer}
                            isWebComponentVersion={isWebComponentVersion}
                            isV7SearchEngineVersion={isV7SearchEngineVersion}
                            autocompleteData={seAutocompleteData}
                            {...seMobileProps}
                        />
                    </>
                ) : (
                    searchEngineDesktopContent
                )}
            </form>
        </Box>
    );
};
